import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { TargetedPointerEvent } from 'preact';
import type { Field } from '../../shared/types';

interface Drag {
  id: string; pointerId: number; startY: number; startScroll: number; clientY: number;
  active: boolean; offsetY: number; beforeId: string | null;
}

/** The handle owns pointer capture; the question order changes only when dropped. */
export function useQuestionDrag(fields: Field[], onChange: (next: Field[]) => void) {
  const list = useRef<HTMLOListElement>(null);
  const current = useRef({ fields, onChange });
  current.current = { fields, onChange };
  const pending = useRef<Drag | null>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [message, setMessage] = useState('');

  const reorder = (id: string, beforeId: string | null) => {
    const { fields, onChange } = current.current;
    const field = fields.find(f => f.id === id);
    if (!field || field.type === 'section' || id === beforeId) return;
    const rest = fields.filter(f => f.id !== id);
    const at = beforeId === null ? rest.length : rest.findIndex(f => f.id === beforeId);
    if (at < 0 || fields[at]?.id === id) return;
    const next = [...rest.slice(0, at), field, ...rest.slice(at)];
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && list.current?.contains(focused)) restoreFocus.current = focused;
    onChange(next);
    setMessage(`Moved ${field.label || 'untitled question'} to position ${at + 1} of ${fields.length}.`);
  };
  const cancel = () => { pending.current = null; setDrag(null); };
  const update = () => {
    const d = pending.current;
    if (!d) return;
    const offsetY = d.clientY - d.startY + window.scrollY - d.startScroll;
    if (!d.active && Math.abs(offsetY) < 4) return;
    // Compare against the centers of every other card, including collapsed ones.
    const cards = [...(list.current?.querySelectorAll<HTMLElement>(':scope > .q') ?? [])].filter(el => el.dataset.questionId !== d.id);
    const beforeId = cards.find(el => { const box = el.getBoundingClientRect(); return d.clientY < box.top + box.height / 2; })?.dataset.questionId ?? null;
    pending.current = { ...d, active: true, offsetY, beforeId };
    setDrag(pending.current);
  };
  const start = (event: TargetedPointerEvent<HTMLButtonElement>, id: string) => {
    if (current.current.fields.find(field => field.id === id)?.type === 'section') return;
    if (!event.isPrimary || event.button !== 0 || current.current.fields.length < 2 || pending.current) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    pending.current = { id, pointerId: event.pointerId, startY: event.clientY, startScroll: window.scrollY, clientY: event.clientY, active: false, offsetY: 0, beforeId: null };
  };
  const move = (event: TargetedPointerEvent<HTMLButtonElement>) => {
    if (pending.current?.pointerId !== event.pointerId) return;
    pending.current.clientY = event.clientY;
    update();
  };
  const finish = (event: TargetedPointerEvent<HTMLButtonElement>) => {
    if (pending.current?.pointerId !== event.pointerId) return;
    if (pending.current.active) {
      pending.current.clientY = event.clientY; update();
      reorder(pending.current!.id, pending.current!.beforeId);
    }
    cancel();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  // Moving a keyed DOM card can blur its handle; retain focus for subsequent keyboard moves.
  useLayoutEffect(() => {
    restoreFocus.current?.focus({ preventScroll: true });
    restoreFocus.current = null;
  }, [fields]);

  useEffect(() => {
    if (!drag) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); cancel(); } };
    window.addEventListener('keydown', escape);
    window.addEventListener('blur', cancel);
    // Scroll while the pointer is held near a viewport edge, even if it stops moving.
    let frame: number;
    let previous = 0;
    const scroll = (time: number) => {
      const d = pending.current;
      if (!d?.active) return;
      const box = list.current?.getBoundingClientRect();
      const elapsed = previous ? Math.min(time - previous, 32) : 16;
      previous = time;
      const edge = Math.min(80, window.innerHeight / 4);
      const speed = d.clientY < edge && box && box.top < edge ? -Math.min(1, (edge - d.clientY) / edge)
        : d.clientY > window.innerHeight - edge && box && box.bottom > window.innerHeight - edge ? Math.min(1, (d.clientY - window.innerHeight + edge) / edge) : 0;
      if (speed) { window.scrollBy(0, speed * elapsed * 0.8); update(); }
      frame = requestAnimationFrame(scroll);
    };
    frame = requestAnimationFrame(scroll);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('keydown', escape); window.removeEventListener('blur', cancel); };
  }, [!!drag]);
  useEffect(() => { if (pending.current && !fields.some(f => f.id === pending.current!.id)) cancel(); }, [fields]);

  return { list, drag, message, reorder, start, move, finish, cancel };
}
